import React from 'react';
import PropTypes from 'prop-types';
import { STORAGE, MESSAGES } from 'src/constants';
import {
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TooltipMUI6 from 'src/components/TooltipMUI6';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import CopyText from 'src/components/CopyText';
import PopConfirm from 'src/components/PopConfirm';
import TextShowMore from 'src/components/TextShowMore';
import {
  TEMPLATE_CONTEXT_SCOPE,
  getTemplateContextScopeConfig,
} from 'src/constants/templateContextScope';
import helpers from 'src/helpers';
import MoreAttachmentsBadge, { countAdditionalAttachments } from 'src/components/MoreAttachmentsBadge';

const { VCARD_FILENAME } = MESSAGES;

/**
 * Check if attachment is a Contact Card
 */
function isContactCardAttachment(fileName) {
  return fileName === VCARD_FILENAME;
}

/**
 * Strip datetime prefix from filename (e.g., 2026-01-21-16-08-15-77-file.pdf -> file.pdf)
 */
function stripDatetimePrefix(fileName) {
  if (!fileName) return '';
  const datetimePrefixPattern = /^\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2,3}-/;
  return fileName.replace(datetimePrefixPattern, '');
}

/**
 * Get file extension from filename
 */
function getFileExtension(fileName) {
  if (!fileName) return '';
  return fileName.toLowerCase().split('.').pop();
}

/**
 * Check if file is an image type
 */
function isImageFile(fileName) {
  const ext = getFileExtension(fileName);
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
}

/**
 * Check if file is an audio type
 */
function isAudioFile(fileName) {
  const ext = getFileExtension(fileName);
  return ['mp3', 'm4a', 'wav', 'ogg', 'aac', 'wma', 'flac'].includes(ext);
}

function TBodyResultsMessage({
  handleRowClick,
  handleClickShowMoreButton,
  isShowMoreTextChecked,
  message,
  classes,
  userId,
  azAccountName,
  databaseBeingUsed,
  canShareMessageTemplates = false,
  canEditMessageTemplates = false,
  onDuplicate = () => {},
}) {
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const {
    id,
    code,
    description,
    systemGenMessage,
    attachment,
    attachmentContainer,
    additionalAttachments,
    contextScope,
    shareCode,
  } = message;

  return (
    <TableRow
      key={id}
      onClick={(event) => {
        handleRowClick(event, message);
      }}
      className={classes.tableRow}
      hover
    >
      {/* Msg # */}
      <TableCell
        align="left"
        className={classes.msgNum}
      >
        {code}
      </TableCell>

      {/* Scenario */}
      <TableCell
        align="left"
        className={classes.scenario}
      >
        {description}
      </TableCell>

      {/* CON-1621 — Where To Use. Most templates are contact scope, so
          that chip stays muted; the constrained ones also carry a
          plain-language line explaining the limit. */}
      <TableCell
        align="left"
        className={classes.contextScope}
      >
        {(() => {
          const scopeConfig = getTemplateContextScopeConfig(contextScope);
          const isContactScope = (contextScope || TEMPLATE_CONTEXT_SCOPE.CONTACT)
            === TEMPLATE_CONTEXT_SCOPE.CONTACT;
          return (
            <Box>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  px: 1,
                  borderRadius: '10px',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                  backgroundColor: scopeConfig.bg,
                  color: scopeConfig.color,
                }}
              >
                {scopeConfig.label}
              </Box>
              {!isContactScope && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {scopeConfig.explanation}
                </Typography>
              )}
            </Box>
          );
        })()}
      </TableCell>

      {/* Text Message To Send */}
      <TableCell
        align="left"
        className={classes.textMessages}
      >
        <TextShowMore
          text={systemGenMessage || ''}
          maxTextLength={350}
          checked={isShowMoreTextChecked(id)}
          setChecked={handleClickShowMoreButton}
          orderId={id}
        />
      </TableCell>

      {/* Image */}
      <TableCell
        align="left"
        className={classes.image}
      >
        {attachment ? (
          (() => {
            if (isContactCardAttachment(attachment)) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    background: '#F0F7FF',
                    border: '1px solid #1976D2',
                    borderRadius: '4px',
                    padding: '6px 10px',
                    maxWidth: 140,
                  }}
                >
                  <ContactPageIcon sx={{ color: '#1976D2', fontSize: 24 }} />
                  <Typography variant="body2" fontWeight={500} noWrap>
                    Your Contact Card
                  </Typography>
                </Box>
              );
            }
            if (isImageFile(attachment)) {
              return (
                <img
                  src={helpers.azure.getImageFileURL(attachment, userId, azAccountName, STORAGE.sas, databaseBeingUsed, attachmentContainer, true)}
                  alt={attachment}
                />
              );
            }
            // PDF, audio, or other file type
            const ext = getFileExtension(attachment);
            const displayName = stripDatetimePrefix(attachment);
            const isAudio = isAudioFile(attachment);
            let FileIcon = InsertDriveFileIcon;
            let iconColor = '#757575';
            if (ext === 'pdf') {
              FileIcon = PictureAsPdfIcon;
              iconColor = '#d32f2f';
            } else if (isAudio) {
              FileIcon = AudioFileIcon;
              iconColor = '#7B1FA2';
            }
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  background: '#F5F5F5',
                  border: '1px solid #E0E0E0',
                  borderRadius: '4px',
                  padding: '6px 10px',
                  maxWidth: 160,
                }}
              >
                <FileIcon sx={{ color: iconColor, fontSize: 24 }} />
                <Typography
                  variant="body2"
                  noWrap
                  title={displayName}
                  sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {displayName}
                </Typography>
              </Box>
            );
          })()
        ) : ''}
        {/* CON-1936 — files 2..N on the template (multi-attachment beta). */}
        <MoreAttachmentsBadge count={countAdditionalAttachments(attachment, additionalAttachments)} />
      </TableCell>

      {/* CON-1625 — Share Code. CopyText is the convention every other
          shareable list uses; the code is minted lazily, so a template that
          has never been shared shows nothing until it is. */}
      {canShareMessageTemplates && (
        <TableCell align="left" className={classes.shareCode} onClick={(e) => e.stopPropagation()}>
          {shareCode ? <CopyText text={shareCode} /> : null}
        </TableCell>
      )}

      {/* CON-1625 — row actions. Duplicate is ordinary CRUD: a duplicate
          carries NULL provenance, so it is an independent template rather
          than a share lineage, and it must not be dark behind the share
          rollout. This condition MUST match THeadResults' actions column. */}
      {canEditMessageTemplates && (
        <TableCell align="left" className={classes.actions} onClick={(e) => e.stopPropagation()}>
          <TooltipMUI6 title="More actions">
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </TooltipMUI6>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <PopConfirm
              title="This will create a copy of this Message Template. Continue?"
              onConfirm={() => {
                setMenuAnchor(null);
                onDuplicate(id);
              }}
            >
              <MenuItem>
                <FileCopyOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
                Duplicate
              </MenuItem>
            </PopConfirm>
          </Menu>
        </TableCell>
      )}
    </TableRow>
  );
}

TBodyResultsMessage.propTypes = {
  userId: PropTypes.number.isRequired,
  azAccountName: PropTypes.string.isRequired,
  databaseBeingUsed: PropTypes.string.isRequired,
  handleRowClick: PropTypes.func.isRequired,
  handleClickShowMoreButton: PropTypes.func.isRequired,
  isShowMoreTextChecked: PropTypes.func.isRequired,
  message: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  canShareMessageTemplates: PropTypes.bool,
  canEditMessageTemplates: PropTypes.bool,
  onDuplicate: PropTypes.func,
};

export default TBodyResultsMessage;
